import { Api, coreModuleName, FetchArgs } from "@reduxjs/toolkit/query";
import { createApi, reactHooksModuleName } from "@reduxjs/toolkit/query/react";
import keyMirror from "keymirror";
import qs from "qs";

import { backendAddress, chatServiceAddress } from "@/common/utilities/env";
import { STRINGIFY_OPTIONS } from "@/common/utilities/url";
import {
  createBaseQuery,
  type GenFiveBaseQuery,
} from "@/state/api/createBaseQuery";
import { LAYOUT_POLICY } from "@/state/api/layoutPolicy";

export const TAG_TYPES = keyMirror({
  ANNOTATIONS: null,
  ANNOTATION_LANGUAGES: null,
  CONNECTIONS: null,
  NODES: null,
  CONTENTS: null,
  MEDIA: null,
  MEDIA_SETTINGS: null,
  STAKEHOLDERS: null,
  STAKEHOLDER_SAVED_FILTERS: null,
  STAKEHOLDER_STATS: null,
  STAKEHOLDER_GROUPS: null,
  STAKEHOLDER_GROUP_TAGS: null,
  ORGANIZATIONS: null,
  ISSUES: null,
  ORGANIZATION_ISSUES: null,
  ORGANIZATION_SENTIMENT_SETTINGS: null,
  PROJECT_ISSUES: null,
  PROJECT_MEDIA_SAVED_FILTER: null,
  PROJECT_ROLE: null, // Also known as workspace sharing
  PROJECT: null,
  ROLES: null,
  USERS: null,
  TOUCHPOINT: null,
  TOUCHPOINT_ENTITIES: null,
  TOUCHPOINT_SAVED_FILTERS: null,
  PLAN: null,
  PLAN_ENTITIES: null,
  INSIGHT: null,
  TOPIC_FEEDBACK: null,
  CUSTOM_FIELD: null,
  ASSET: null,
  TASK: null,
  ATTACHMENT: null,
  TASK_ENTITIES: null,
  USER_GROUP: null,
  ACTIVITY: null,
  NEWSLETTER: null,
  NEWSLETTER_COMMENTS: null,
  WORKSPACE_CONTENT_SETTINGS: null,
  WORKSPACE_ISSUES: null,
  GENFIVE_INSIGHT_TOPIC_FEEDBACK: null,
  GENFIVE_INSIGHT_NETWORK: null,
  GENFIVE_INSIGHT_NETWORK_GRAPH: null,
  GENFIVE_INSIGHT_NETWORK_TAG: null,
  GENFIVE_INSIGHT_NETWORK_HISTORY: null,
  GENFIVE_INSIGHT_WORKSPACE_CUSTOMIZATION: null,
  GENFIVE_INSIGHT_USER_DASHBOARD: null,
  GENFIVE_INSIGHT_WIDGET: null,
  ENGAGEMENT_PROJECTS_FOLDERS: null,
  EXTRACTION_JOBS: null,
  POLICY: null,
  AUDIT_LOG_plan: null,
  AUDIT_LOG_touchpoint: null,
  // Admin-only routes (apps/admin/api/adminOnly, DEDUPE-2 B8).
  MANAGEMENT_ROLE: null,
  PERMISSION: null,
  OBJECT_USER: null,
  FEATURE_FLAG: null,
  SUPERPROMPTS: null,
  USER_ACTION_LOGS: null,
  ADMIN_NETWORKS: null,
  ADMIN_USER_DASHBOARDS: null,
  // GEN5-5298: the admin console GeniePlus Usage (accounts, analytics, pricing).
  GENIE_PLUS_ADMIN: null,
  // GEN5-5298: the member's GeniePlus offer and usage, refreshed after each turn.
  GENIE_PLUS_ME: null,
  // GEN5-5298: an organization's own Genie usage page (organization.manage).
  GENIE_PLUS_ORGANIZATION: null,
});

export const TAG_IDS = keyMirror({ LIST: null });

// Additional tag constants for backward compatibility
export const TAG_ID__LIST = "LIST";

const matchAndReplaceApi = (
  args: string | FetchArgs,
  matchUrlStart: string,
  replacement: string
): string | FetchArgs => {
  if (typeof args === "string") {
    return args.startsWith(matchUrlStart)
      ? args.replace(matchUrlStart, replacement)
      : args;
  }
  return args.url.startsWith(matchUrlStart)
    ? { ...args, url: args.url.replace(matchUrlStart, replacement) }
    : args;
};

/** Services on this machine, for local development (`REACT_APP_*_LOCAL_API`, workspace-setup/README.md "Settings"). */
const toLocalServices = (args: string | FetchArgs): string | FetchArgs => {
  let finalArgs = args;
  if (import.meta.env.REACT_APP_INSIGHTS_LOCAL_API === "yes") {
    finalArgs = matchAndReplaceApi(
      finalArgs,
      "insights/api/v1",
      "http://localhost:8086/insights/api/v1"
    );
  }

  // Genie's chat-service on this machine (genie up genie-plus, workspace-setup/README.md).
  // The same address the chat stream uses (`chatServiceAddress`).
  if (chatServiceAddress() !== backendAddress()) {
    finalArgs = matchAndReplaceApi(
      finalArgs,
      "chat/api/v1",
      `${chatServiceAddress()}/chat/api/v1`
    );
  }

  if (import.meta.env.REACT_APP_ENGAGEMENTS_LOCAL_API === "yes") {
    finalArgs = matchAndReplaceApi(
      finalArgs,
      "engagements/api/v1",
      "http://localhost:8085/engagements/api/v1"
    );
  }

  if (import.meta.env.REACT_APP_STAKEHOLDERS_LOCAL_API === "yes") {
    finalArgs = matchAndReplaceApi(
      finalArgs,
      "stakeholders/api/v1",
      "http://localhost:8081/stakeholders/api/v1"
    );
  }
  return finalArgs;
};

export type { GenFiveBaseQuery } from "@/state/api/createBaseQuery";

/**
 * Stable, hand-written surface type for {@link genFiveApi}.
 *
 * `createApi` infers an extremely deep type; pinning the shape here keeps
 * `injectEndpoints` in feature files fast and consistent to type-check.
 *
 * `TagTypes` is intentionally `any`: several endpoints provide / invalidate
 * tags produced by helpers (audit log, sharing) that live outside `TAG_TYPES`.
 */
export type GenFiveApi = Api<
  GenFiveBaseQuery,
  {},
  "genFiveApi",
  any,
  typeof coreModuleName | typeof reactHooksModuleName
>;

// Initialize an empty api service that we'll inject endpoints into later as needed.
// Its behaviour around a request (permissions, notifications) is the app's policy, given to
// the store (ARCHITECTURE item 3); the wire format (qs params, local services) is the API's.
const genFiveApi: GenFiveApi = createApi({
  reducerPath: "genFiveApi",
  baseQuery: createBaseQuery({
    fallbackPolicy: LAYOUT_POLICY,
    paramsSerializer: (params) => qs.stringify(params, STRINGIFY_OPTIONS),
    rewriteArgs: toLocalServices,
  }),
  tagTypes: Object.values(TAG_TYPES),
  endpoints: () => ({}),
});

// Legacy tag type definitions for backward compatibility
export const TAG_TYPE = {
  ANNOTATIONS: "ANNOTATIONS",
  ANNOTATION_LANGUAGES: "ANNOTATION_LANGUAGES",
  CONNECTIONS: "CONNECTIONS",
  NODES: "NODES",
  CONTENTS: "CONTENTS",
  STAKEHOLDERS: "STAKEHOLDERS",
  STAKEHOLDER_SAVED_FILTERS: "STAKEHOLDER_SAVED_FILTERS",
  STAKEHOLDER_STATS: "STAKEHOLDER_STATS",
  STAKEHOLDER_GROUPS: "STAKEHOLDER_GROUPS",
  STAKEHOLDER_GROUP_TAGS: "STAKEHOLDER_GROUP_TAGS",
  ORGANIZATION_ISSUES: "ORGANIZATION_ISSUES",
  WORKSPACE_CONTENT_SETTINGS: "WORKSPACE_CONTENT_SETTINGS",
  WORKSPACE_ISSUES: "WORKSPACE_ISSUES",
} as const;

export const GENFIVE_LAYOUT_TAG_TYPE = {
  PROJECT_ISSUES: "PROJECT_ISSUES",
  PROJECT_MEDIA_SAVED_FILTER: "PROJECT_MEDIA_SAVED_FILTER",
} as const;

export const GENFIVE_ENGAGEMENT_TAG_TYPE = {
  TOUCHPOINT_SAVED_FILTERS: "TOUCHPOINT_SAVED_FILTERS",
} as const;

export default genFiveApi;

export type BaseQueryArgs = {
  organizationId: number;
  workspaceId: number;
};

/** @deprecated Prefer {@link BaseQueryArgs} with resolved numeric ids. */
export type IDeprecatedBaseQueryArgs = {
  organizationId: number | string | undefined;
  workspaceId: number | string | undefined;
};
